import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  Alert,
  CircularProgress,
  Fade,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Work as WorkIcon,
  AttachMoney as MoneyIcon,
  Code as CodeIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  createProject, 
  getCategories, 
  Category 
} from '../lib/api/projects';
import { getCurrentUserProfile } from '../lib/api/profile';
import { trackProjectCreation } from '../lib/analytics';

interface ProjectFormData {
  title: string;
  description: string;
  budget_min: number | null;
  budget_max: number | null;
  deadline: string;
  category_id: string;
  skills: string[];
}

const NewProjectPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
  
  // Form data
  const [formData, setFormData] = useState<ProjectFormData>({
    title: '',
    description: '',
    budget_min: null,
    budget_max: null,
    deadline: '',
    category_id: '',
    skills: []
  });

  // Skills options
  const skillOptions = [
    'React', 'Vue.js', 'Angular', 'JavaScript', 'TypeScript', 'Node.js', 'Python', 'Java', 'C#', 'PHP',
    'HTML', 'CSS', 'SASS', 'SCSS', 'Bootstrap', 'Material-UI', 'Tailwind CSS', 'jQuery',
    'MongoDB', 'PostgreSQL', 'MySQL', 'Redis', 'Firebase', 'Supabase',
    'AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'Git', 'GitHub', 'GitLab',
    'Figma', 'Adobe XD', 'Sketch', 'Photoshop', 'Illustrator',
    'WordPress', 'Shopify', 'WooCommerce', 'Magento', 'Drupal',
    'Mobile Development', 'iOS', 'Android', 'React Native', 'Flutter', 'Xamarin',
    'Machine Learning', 'AI', 'Data Science', 'Blockchain', 'Web3', 'NFT',
    'E-commerce', 'Payment Integration', 'API Development', 'REST API', 'GraphQL',
    'DevOps', 'CI/CD', 'Testing', 'Jest', 'Cypress', 'Selenium'
  ];

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check user role
      const userProfile = await getCurrentUserProfile();
      if (!userProfile) {
        setError('Трябва да влезете в профила си, за да публикувате проект');
        return;
      }
      
      if (userProfile.role !== 'CLIENT') {
        setError('Само клиенти могат да публикуват проекти');
        return;
      }
      
      setUserRole(userProfile.role);
      
      // Load categories
      console.log('Loading categories...');
      const categoriesData = await getCategories();
      console.log('Categories loaded:', categoriesData);
      setCategories(categoriesData);
      
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Грешка при зареждане на данните');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ProjectFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSkillsChange = (newSkills: string[]) => {
    setFormData(prev => ({
      ...prev,
      skills: newSkills
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.title.trim()) {
      return 'Заглавието е задължително';
    }
    
    if (formData.title.length < 10) {
      return 'Заглавието трябва да бъде поне 10 символа';
    }
    
    if (!formData.description.trim()) {
      return 'Описанието е задължително';
    }
    
    if (formData.description.length < 50) {
      return 'Описанието трябва да бъде поне 50 символа';
    }
    
    if (formData.budget_min && formData.budget_max && formData.budget_min > formData.budget_max) {
      return 'Минималният бюджет не може да бъде по-голям от максималния';
    }
    
    if (formData.deadline && new Date(formData.deadline) <= new Date()) {
      return 'Крайният срок трябва да бъде в бъдещето';
    }
    
    if (formData.skills.length === 0) {
      return 'Изберете поне едно умение';
    }
    
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const projectData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        budget_min: formData.budget_min || undefined,
        budget_max: formData.budget_max || undefined,
        deadline: formData.deadline || undefined,
        category_id: formData.category_id || undefined,
        skills: formData.skills
      };
      
      console.log('Submitting project data:', projectData);
      const result = await createProject(projectData);
      console.log('Project created successfully:', result);
      
      // Track project creation
      const budgetRange = formData.budget_min && formData.budget_max 
        ? `${formData.budget_min}-${formData.budget_max}`
        : 'not_specified';
      trackProjectCreation(formData.category_id || 'general', budgetRange);
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/find-work');
      }, 2000);
      
    } catch (err: any) {
      console.error('Error creating project:', err);
      const errorMessage = err?.message || err?.error?.message || 'Грешка при създаване на проекта';
      setError(`Грешка: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: '',
      description: '',
      budget_min: null,
      budget_max: null,
      deadline: '',
      category_id: '',
      skills: []
    });
    setError(null);
  };

  if (loading) {
    return (
      <Box sx={{ 
        backgroundColor: '#0f0f23', 
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: '#28A0F0' }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  if (userRole !== 'CLIENT') {
    return (
      <Box sx={{ 
        backgroundColor: '#0f0f23', 
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Header />
        <Container maxWidth="md" sx={{ py: 8, position: 'relative', zIndex: 1 }}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 4,
              p: 4,
              textAlign: 'center'
            }}
          >
            <Typography variant="h4" sx={{ color: '#ef4444', mb: 2 }}>
              Достъп отказан
            </Typography>
            <Typography variant="body1" sx={{ color: '#ffffff', mb: 3 }}>
              Само клиенти могат да публикуват проекти. Моля, влезте с клиентски профил.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{
                background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                color: '#ffffff',
              }}
            >
              Начална страница
            </Button>
          </Card>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: '#0f0f23', 
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Animated background shapes */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 60,
          height: 60,
          background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
          borderRadius: '50%',
          opacity: 0.3,
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(180deg)' },
          },
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '15%',
          width: 40,
          height: 40,
          background: 'linear-gradient(135deg, #8b5cf6 0%, #28A0F0 100%)',
          borderRadius: '20%',
          opacity: 0.4,
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />

      <Header />
      
      <Container maxWidth="lg" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton
              onClick={() => navigate('/find-work')}
              sx={{ color: '#28A0F0', mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <WorkIcon sx={{ color: '#28A0F0', mr: 1, fontSize: '2rem' }} />
            <Typography 
              variant="h3" 
              sx={{ 
                color: '#ffffff', 
                fontWeight: 800,
                background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
                {t('newProject.publish')}
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ color: '#a0a0a0', ml: 6 }}>
            {t('newProject.subtitle')}
          </Typography>
        </Box>

        {/* Success Message */}
        {success && (
          <Fade in={success}>
            <Alert 
              severity="success" 
              sx={{ mb: 3 }}
              onClose={() => setSuccess(false)}
            >
              Проектът е създаден успешно! Пренасочваме към страницата с проекти...
            </Alert>
          </Fade>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Main Form */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: 4,
            backdropFilter: 'blur(10px)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={4}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <DescriptionIcon sx={{ color: '#28A0F0', mr: 1 }} />
                  <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    Основна информация
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('newProject.projectTitle')}
                  placeholder={t('newProject.projectTitlePlaceholder')}
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  helperText={t('newProject.projectTitleHelper')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: '#28A0F0' },
                      '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                    },
                    '& .MuiInputLabel-root': { color: '#a0a0a0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                    '& .MuiFormHelperText-root': { color: '#a0a0a0' },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label={t('newProject.projectDescription')}
                  placeholder={t('newProject.projectDescriptionPlaceholder')}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  helperText={t('newProject.projectDescriptionHelper')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: '#28A0F0' },
                      '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                    },
                    '& .MuiInputLabel-root': { color: '#a0a0a0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                    '& .MuiFormHelperText-root': { color: '#a0a0a0' },
                  }}
                />
              </Grid>

              {/* Budget and Timeline */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                  <MoneyIcon sx={{ color: '#28A0F0', mr: 1 }} />
                  <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    Бюджет и срок
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('newProject.minBudget')}
                  type="number"
                  placeholder="0"
                  value={formData.budget_min || ''}
                  onChange={(e) => handleInputChange('budget_min', e.target.value ? Number(e.target.value) : null)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: '#28A0F0' },
                      '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                    },
                    '& .MuiInputLabel-root': { color: '#a0a0a0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('newProject.maxBudget')}
                  type="number"
                  placeholder="0"
                  value={formData.budget_max || ''}
                  onChange={(e) => handleInputChange('budget_max', e.target.value ? Number(e.target.value) : null)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: '#28A0F0' },
                      '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                    },
                    '& .MuiInputLabel-root': { color: '#a0a0a0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('newProject.deadline')}
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => handleInputChange('deadline', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: '#28A0F0' },
                      '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                    },
                    '& .MuiInputLabel-root': { color: '#a0a0a0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#a0a0a0' }}>Категория</InputLabel>
                  <Select
                    value={formData.category_id}
                    onChange={(e) => handleInputChange('category_id', e.target.value)}
                    sx={{
                      color: '#ffffff',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#28A0F0' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#28A0F0' },
                    }}
                  >
                    <MenuItem value="">Избери категория</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Skills */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                  <CodeIcon sx={{ color: '#28A0F0', mr: 1 }} />
                  <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    Необходими умения
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={skillOptions}
                  value={formData.skills}
                  onChange={(_, newValue) => handleSkillsChange(newValue)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option}
                        label={option}
                        sx={{
                          backgroundColor: 'rgba(40, 160, 240, 0.2)',
                          color: '#28A0F0',
                          border: '1px solid rgba(40, 160, 240, 0.3)',
                          '& .MuiChip-deleteIcon': {
                            color: '#28A0F0',
                          },
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('newProject.addSkills')}
                      placeholder={t('newProject.addSkillsPlaceholder')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          color: '#ffffff',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                          '&:hover fieldset': { borderColor: '#28A0F0' },
                          '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                        },
                        '& .MuiInputLabel-root': { color: '#a0a0a0' },
                        '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    disabled={saving}
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: '#ffffff',
                      '&:hover': {
                        borderColor: '#28A0F0',
                        backgroundColor: 'rgba(40, 160, 240, 0.1)',
                      },
                    }}
                  >
                    Изчисти
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                      color: '#ffffff',
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                      },
                      '&:disabled': {
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    {saving ? 'Публикуване...' : 'Публикувай проект'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>

      <Footer />
    </Box>
  );
};

export default NewProjectPage;
